import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Loader } from '../common/spinner/Loader'
import { getWorkplacesColumns, getWorkplaceUsage, mapWorkplacesSNIs } from '../../helpers/settingsHelper'
import type { SNIWorkplaceConnection, Workplace, Workplace as WorkplaceType } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { api } from '../../helpers/apiHelper'
import { handleMessage } from '../../helpers/stateHelper'
import { t } from '../../i18n/i18n'
import { IOpenCloseModalProps } from '../../interfaces/IOpenCloseModalProps'
import { makeStyles, Button, Text, tokens, Tooltip } from '@fluentui/react-components'
import { AddCircle24Regular } from '@fluentui/react-icons'
import { CustomList } from '../common/list/CustomList'
import { InfoMessageBar } from '../common/other/InfoMessageBar'
import { ModalSettings } from './ModalSettings'
import { ModalUpsertWorkplace } from './ModalUpsertWorkplace'

const useStyles = makeStyles({
   addButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: tokens.spacingVerticalL,
      marginRight: tokens.spacingHorizontalS,

      '@media screen and (max-width: 1007px)': {
         padding: tokens.spacingVerticalM,
      },
   },
   addButtonPhone: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   width: {
      width: '100%',
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '10%',
         },
         ':nth-child(2)': {
            width: '30%',
            minWidth: '300px',
         },
         ':nth-child(3)': {
            width: '20%',
            minWidth: '200px',
         },
         ':nth-child(4)': {
            width: '20%',
            minWidth: '200px',
         },
         ':nth-child(5)': {
            width: '15%',
            minWidth: '150px',
         },
      },
   },
   phoneItem: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'flex',
      },
      flexDirection: 'column',
      padding: `0 ${tokens.spacingHorizontalS}`,
      borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
      paddingBottom: tokens.spacingVerticalL,
      '& > *': {
         marginBottom: tokens.spacingVerticalXXS,
      },
   },
   phoneList: {
      overflowY: 'auto',
      height: 'auto',
      maxHeight: '700px',
   },
})

export const ModalWorkplace = ({ open, closeModal }: IOpenCloseModalProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { hasMsalToken, organisation } = dataContext.state
   const [allWorkplaces, setAllWorkplaces] = useState<WorkplaceType[]>([])
   const [loading, setLoading] = useState(true)
   const [workplaceInUseMap, setWorkplaceInUseMap] = useState<Record<string, boolean>>({})
   const [selectedItem, setSelectedItem] = useState<Workplace>(null)
   const [openModal, setOpenModal] = useState(false)

   const filteredWorkplaces = useMemo(() => allWorkplaces.filter((wp) => wp.isDefault === false), [allWorkplaces])

   useEffect(() => {
      getWorkplaceUsage(filteredWorkplaces, setWorkplaceInUseMap)
   }, [filteredWorkplaces])

   useEffect(() => {
      if (!open) return
      ;(async () => {
         try {
            const [allSnis, workplacesResponse] = await Promise.all([(await api().getAllSnis()).data, api().getWorkplacesByOrganisationId(organisation.id)])
            const mappedWorkplaces = mapWorkplacesSNIs(workplacesResponse.data, allSnis)

            setAllWorkplaces(mappedWorkplaces)
         } catch (error) {
            handleMessage(dataContext.setRootState, 'error', t('SomethingWentWrong'), t('CouldNotFetchData'))
         } finally {
            setLoading(false)
         }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, organisation.id])

   const onRowClick = (item: Workplace) => {
      setOpenModal(true)
      setSelectedItem(item)
   }

   return (
      <>
         <ModalSettings open={open} closeModal={closeModal} title={t('Workplaces')}>
            <div className={classes.addButton}>
               <Button
                  onClick={() => onRowClick(null)}
                  appearance="primary"
                  icon={<AddCircle24Regular />}
                  disabled={loading || allWorkplaces.length > organisation.workplaceLimit} // more than limit because of default workplace also disabled while loading as otherwise button is active before we load the amount of workplaces the org has
                  className={classes.addButtonPhone}
               >
                  {t('Add')}
               </Button>
            </div>

            {loading ? (
               <Loader />
            ) : organisation.workplaceLimit > 0 && filteredWorkplaces.length > 0 ? (
               <>
                  <CustomList
                     className={classes.hideInPhone}
                     columns={getWorkplacesColumns()}
                     items={filteredWorkplaces}
                     onRowClick={onRowClick}
                     rowClassName={classes.row}
                  />
                  <div className={classes.phoneList}>
                     {filteredWorkplaces.map((item, index) => (
                        <div className={classes.phoneItem} key={index} onClick={() => onRowClick(item)}>
                           <Text size={400}>{item.cfar}</Text>
                           <Text size={400} weight="semibold">
                              {item.name}
                           </Text>
                           <Text size={400}>{item.organisationSize?.text}</Text>
                           <Text size={400}>
                              {item.snIs?.map((conn: SNIWorkplaceConnection) => conn.sni?.code.toString().padStart(2, '0').padEnd(5, 'xxx')).join(', ')}
                           </Text>
                           <Text size={400}>{item.description}</Text>{' '}
                        </div>
                     ))}
                  </div>
               </>
            ) : (
               <InfoMessageBar title={t('TimeToCreateYourFirstWorkplace')} />
            )}
         </ModalSettings>

         <ModalUpsertWorkplace
            open={openModal}
            setOpen={setOpenModal}
            setAllWorkplaces={setAllWorkplaces}
            workplace={selectedItem}
            workplaceInUseMap={workplaceInUseMap}
         />
      </>
   )
}
